/**
 * Turn a rule citation into a link to the law itself.
 *
 * Rules emit citations shaped "Regulation (EU) 2024/1689, Article 5(1)(c): …".
 * EU acts have a stable CELEX number : 3 + year + R + 4-digit act number :
 * and EUR-Lex's HTML anchors each article as #art_N. Nothing here is a
 * per-rule table: any EU regulation the corpus adds links itself.
 */
const EU_REGULATION = /Regulation\s*\(EU\)\s*(\d{4})\/(\d{1,4})(?:,\s*Article\s*(\d+))?/i;

export interface LawLink {
  /** "Regulation (EU) 2024/1689, Article 5(1)(c)" : the head of the citation. */
  label: string;
  /** null when the citation is not one we can resolve to a source. */
  href: string | null;
}

/** The leading legal reference: act, then article and paragraphs ("..., Article 15(1)(a)"). */
const LEADING_REFERENCE = /^[^:\n]*?\bArticle\s+\d+[a-z]?(?:\(\w{1,4}\))*/i;

/**
 * The citation head, ahead of the colon. A head longer than a label (a rule
 * that adds its purpose, "Article 15, block indirect prompt injection ...",
 * or the article's title) is cut to its leading reference, so the label still
 * names the article its link opens rather than falling back to the rule ID
 * (DASH-050: rego-art15-ipi-003/008 and rego-art16-001 showed their rule ID).
 */
export function lawReference(citation: string | null, fallback: string): string {
  if (!citation) return fallback;
  const head = citation.split(':')[0].trim();
  if (head.length > 0 && head.length <= 80) return head;
  const lead = LEADING_REFERENCE.exec(citation.trim())?.[0].trim();
  return lead && lead.length <= 80 ? lead : fallback;
}

export function lawLink(citation: string | null, fallback: string): LawLink {
  const label = lawReference(citation, fallback);
  const m = citation ? EU_REGULATION.exec(citation) : null;
  if (!m) return { label, href: null };
  const [, year, number, article] = m;
  const celex = `3${year}R${number.padStart(4, '0')}`;
  const anchor = article ? `#art_${article}` : '';
  return {
    label,
    href: `https://eur-lex.europa.eu/legal-content/EN/TXT/HTML/?uri=CELEX:${celex}${anchor}`,
  };
}

/** Text after the citation head: the article's own words. */
export const articleText = (citation: string | null): string => {
  if (!citation || !citation.includes(':')) return '';
  return citation.slice(citation.indexOf(':') + 1).trim();
};

/** The article's opening clause, capped so the lead stays one or two lines. */
const LEAD_MAX = 220;
export const firstSentence = (text: string): string => {
  const m = /^(.*?[.;])\s/.exec(text);
  const lead = m ? m[1] : text;
  if (lead.length <= LEAD_MAX) return lead;
  const cut = lead.slice(0, LEAD_MAX);
  return `${cut.slice(0, cut.lastIndexOf(' '))}…`;
};
